import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { date as fmtDate, isoDate, num } from '@/lib/format';
import { meterSuffix, unitOf } from '@/lib/meter';
import { Button } from '@/components/ui/Button';
import { NumberInput, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import type { Machine, MachineTransfer, Site } from '@/types/models';

/**
 * Moving a vehicle to another site (0036). A vehicle's site is never a
 * plain field: the move is recorded with its date and meter reading, and
 * the database refuses it while a breakdown is open.
 */
export function TransferPanel({ machine, onCancel, onDone }: {
  machine: Machine;
  onCancel: () => void;
  onDone: () => void | Promise<void>;
}) {
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  const targets = (sites ?? []).filter((s) =>
    (s.kind === 'quarry' || s.kind === 'branch') && s.is_active && s.id !== machine.site_id && siteOk(s.id));
  const [to, setTo] = useState<string | null>(null);
  const [day, setDay] = useState(isoDate());
  const [reading, setReading] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sfx = meterSuffix(unitOf(machine));

  async function apply() {
    if (!to) return;
    setBusy(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc('transfer_machine', {
        p_machine: machine.id, p_to_site: to, p_date: day, p_reading: reading, p_note: note.trim() || null,
      });
      if (err) throw err;
      await syncNow();
      toast.success(`${machine.fleet_no} moved to ${(data as { to?: string })?.to ?? 'the new site'}`);
      await onDone();
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'The vehicle was not moved');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="meter-change field--full" data-testid="transfer-panel">
      <p className="small" style={{ marginTop: 0 }}>
        Move <strong>{machine.fleet_no}</strong> to another site. Its history stays where it happened;
        from the move on, it counts at the new site. Not possible while a breakdown is open.
      </p>
      <div className="form-grid">
        <SelectInput id="tr-to" label="Move to" required value={to}
          options={targets.map((s) => ({ value: s.id, label: s.name }))} onChange={setTo} />
        <TextInput id="tr-date" label="Date of the move" type="date" required value={day}
          onChange={(x) => setDay(x || isoDate())} />
        <NumberInput id="tr-reading" label="Meter at the move" suffix={sfx} min={0}
          value={reading} onChange={setReading}
          help={`Blank keeps ${num(machine.current_hour_meter, unitOf(machine) === 'km' ? 0 : 1)} ${sfx}`} />
        <div className="field--full">
          <TextArea id="tr-note" label="Reason" value={note} onChange={setNote} />
        </div>
      </div>
      {error && <div className="issue issue--block" style={{ display: 'block', marginTop: 'var(--sp-2)' }}>{error}</div>}
      <div className="row" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end', marginTop: 'var(--sp-3)' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={() => void apply()} loading={busy} disabled={!to}>Move vehicle</Button>
      </div>
    </div>
  );
}

/** Where the vehicle has been. */
export function TransferHistory({ machineId }: { machineId: string }) {
  const rows = useRows<MachineTransfer>('machine_transfers');
  const sites = useRows<Site>('sites');
  const name = (id: string) => (sites ?? []).find((s) => s.id === id)?.name ?? '—';
  const mine = (rows ?? []).filter((r) => r.machine_id === machineId)
    .sort((a, b) => b.transferred_on.localeCompare(a.transferred_on));
  if (mine.length === 0) return null;
  return (
    <div className="field--full small muted" style={{ marginTop: 'var(--sp-1)' }}>
      {mine.map((t) => (
        <div key={t.id}>
          {fmtDate(t.transferred_on)}: {name(t.from_site_id)} → {name(t.to_site_id)}
          {t.meter_reading != null ? ` at ${num(t.meter_reading, 0)}` : ''}{t.notes ? ` — ${t.notes}` : ''}
        </div>
      ))}
    </div>
  );
}
